import {
  Component,
  inject,
  Input,
  Output,
  EventEmitter,
  SimpleChanges,
  ViewChild,
  OnInit,
  OnDestroy,
  OnChanges,
} from "@angular/core";
import { MatTooltipModule } from "@angular/material/tooltip";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexDataLabels,
  ApexPlotOptions,
  ApexYAxis,
  ApexLegend,
  ApexStroke,
  ApexXAxis,
  ApexFill,
  ApexTooltip,
  NgApexchartsModule,
  ChartComponent,
} from "ng-apexcharts";
import { DashboardChartEmptyStateComponent } from "../../shared/dashboard-chart-empty-state/dashboard-chart-empty-state.component";

export type ChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  dataLabels: ApexDataLabels;
  plotOptions: ApexPlotOptions;
  yaxis: ApexYAxis;
  xaxis: ApexXAxis;
  fill: ApexFill;
  tooltip: ApexTooltip;
  stroke: ApexStroke;
  legend: ApexLegend;
  colors: string[];
};

@Component({
  selector: "app-oportunity-value-chart",
  standalone: true,
  imports: [NgApexchartsModule, TranslateModule, MatTooltipModule, DashboardChartEmptyStateComponent],
  templateUrl: "./oportunity-value-chart.component.html",
  styleUrl: "./oportunity-value-chart.component.scss",
})
export class OportunityValueChartComponent implements OnInit, OnDestroy, OnChanges {
  translateSvc = inject(TranslateService);
  @ViewChild("chart") chart: ChartComponent;

  @Input({ required: true }) filedNoReimbursementTotal: number = 0;
  @Input({ required: true }) unFiledClaimTotal: number = 0;
  @Input({ required: true }) filedNoReimbursementAmount: number = 0;
  @Input({ required: true }) unFiledClaimAmount: number = 0;

  @Output() readonly claimsNotFiledSelected = new EventEmitter<void>();
  @Output() readonly filedCasesNotReimbursedSelected = new EventEmitter<void>();

  legendItems: {
    key: "claims" | "cases";
    labelKey: string;
    color: string;
  }[] = [
    { key: "claims", labelKey: "dashboard.claims_not_filed", color: "" },
    {
      key: "cases",
      labelKey: "dashboard.filed_cases_not_reimbursed",
      color: "",
    },
  ];

  get hasData(): boolean {
    return this.filedNoReimbursementTotal > 0 || this.unFiledClaimTotal > 0;
  }

  public chartOptions: Partial<ChartOptions>;
  private mutationObserver: MutationObserver;
  private readonly chartHeight = 190;

  constructor() {
    this.chartOptions = {
      series: [
        {
          data: [0, 0],
        },
      ],
      chart: {
        type: "bar",
        height: this.chartHeight,
        width: "100%",
        toolbar: {
          show: false,
        },
        animations: {
          enabled: false,
        },
        parentHeightOffset: 0,
        events: {
          dataPointSelection: (
            _event: unknown,
            _chartContext: unknown,
            config: { dataPointIndex: number },
          ) => {
            this.onChartBarSelected(config.dataPointIndex);
          },
        },
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: "45%",
          borderRadius: 14,
          borderRadiusApplication: "end",
          distributed: true,
        },
      },
      dataLabels: {
        enabled: true,
        offsetY: -12,
        formatter: (val: number) => `${val}`,
        style: {
          fontSize: "10px",
          fontWeight: "bold",
        },
      },
      stroke: {
        show: false,
      },
      xaxis: {
        categories: [
          this.translateSvc.instant("dashboard.claims_not_filed"),
          this.translateSvc.instant("dashboard.filed_cases_not_reimbursed"),
        ],
        labels: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
        axisBorder: {
          show: false,
        },
      },
      yaxis: {
        title: {
          text: this.translateSvc.instant("dashboard.claims"),
          style: {
            fontSize: "11px",
            fontWeight: 600,
          },
        },
        labels: {
          style: {
            fontSize: "10px",
          },
        },
      },
      fill: {
        opacity: 1,
      },
      tooltip: {
        enabled: true,
        theme: "light",
        custom: ({ seriesIndex, dataPointIndex, w }: any) => {
          const labels = [
            this.translateSvc.instant("dashboard.claims_not_filed"),
            this.translateSvc.instant("dashboard.filed_cases_not_reimbursed"),
          ];
          const totals = [
            this.unFiledClaimTotal,
            this.filedNoReimbursementTotal,
          ];
          const amounts = [
            this.unFiledClaimAmount,
            this.filedNoReimbursementAmount,
          ];

          const label = labels[dataPointIndex] || "";
          const total = totals[dataPointIndex] || 0;
          const amount = amounts[dataPointIndex] || 0;
          const color = w.config.colors?.[dataPointIndex] || "#FFD54F";

          const totalLabel = this.translateSvc.instant(
            "dashboard.tooltip.total"
          );
          const amountLabel = this.translateSvc.instant(
            "dashboard.tooltip.amount"
          );

          const amountFormatted = new Intl.NumberFormat(undefined, {
            style: "currency",
            currency: "USD",
            maximumFractionDigits: 2,
          }).format(amount);

          return (
            `<div style="padding:10px;font-size:12px;background:${color};border-radius:4px;color:white;box-shadow:0 4px 12px rgba(0,0,0,0.15);">` +
            `<div style="font-weight:700;margin-bottom:6px">${label}</div>` +
            `<div style="margin-bottom:4px"><span>${totalLabel}:</span> <strong>${total}</strong></div>` +
            `<div><span>${amountLabel}:</span> <strong>${amountFormatted}</strong></div>` +
            `</div>`
          );
        },
      },
      legend: {
        show: false,
      },
      colors: [], // populated dynamically
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["filedNoReimbursementTotal"] || changes["unFiledClaimTotal"]) {
      this.updateSeries();
      this.updateColorsFromTheme();
    }
  }

  ngOnInit(): void {
    this.updateColorsFromTheme();

    this.mutationObserver = new MutationObserver(() => {
      this.updateColorsFromTheme();
    });

    this.mutationObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    this.updateSeries();
    this.translateSvc.onLangChange.subscribe(() => {
      this.chartOptions = {
        series: [
          {
            data: [0, 0],
          },
        ],
        chart: {
          type: "bar",
          height: this.chartHeight,
          width: "100%",
          toolbar: {
            show: false,
          },
          animations: {
            enabled: false,
          },
          parentHeightOffset: 0,
          events: {
            dataPointSelection: (
              _event: unknown,
              _chartContext: unknown,
              config: { dataPointIndex: number },
            ) => {
              this.onChartBarSelected(config.dataPointIndex);
            },
          },
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: "45%",
            borderRadius: 14,
            borderRadiusApplication: "end",
            distributed: true,
          },
        },
        dataLabels: {
          enabled: true,
          offsetY: -12,
          formatter: (val: number) => `${val}`,
          style: {
            fontSize: "10px",
            fontWeight: "bold",
          },
        },
        stroke: {
          show: false,
        },
        xaxis: {
          categories: [
            this.translateSvc.instant("dashboard.claims_not_filed"),
            this.translateSvc.instant("dashboard.filed_cases_not_reimbursed"),
          ],
          labels: {
            show: false,
          },
          axisTicks: {
            show: false,
          },
          axisBorder: {
            show: false,
          },
        },
        yaxis: {
          title: {
            text: this.translateSvc.instant("dashboard.claims"),
            style: {
              fontSize: "11px",
              fontWeight: 600,
            },
          },
          labels: {
            style: {
              fontSize: "10px",
            },
          },
        },
        fill: {
          opacity: 1,
        },
        tooltip: {
          enabled: true,
          theme: "light",
          custom: ({ seriesIndex, dataPointIndex, w }: any) => {
            const labels = [
              this.translateSvc.instant("dashboard.claims_not_filed"),
              this.translateSvc.instant("dashboard.filed_cases_not_reimbursed"),
            ];
            const totals = [
              this.unFiledClaimTotal,
              this.filedNoReimbursementTotal,
            ];
            const amounts = [
              this.unFiledClaimAmount,
              this.filedNoReimbursementAmount,
            ];

            const label = labels[dataPointIndex] || "";
            const total = totals[dataPointIndex] || 0;
            const amount = amounts[dataPointIndex] || 0;
            const color = w.config.colors?.[dataPointIndex] || "#FFD54F";

            const totalLabel = this.translateSvc.instant(
              "dashboard.tooltip.total"
            );
            const amountLabel = this.translateSvc.instant(
              "dashboard.tooltip.amount"
            );

            const amountFormatted = new Intl.NumberFormat(undefined, {
              style: "currency",
              currency: "USD",
              maximumFractionDigits: 2,
            }).format(amount);

            return (
              `<div style="padding:10px;font-size:12px;background:${color};border-radius:4px;color:white;box-shadow:0 4px 12px rgba(0,0,0,0.15);">` +
              `<div style="font-weight:700;margin-bottom:6px">${label}</div>` +
              `<div style="margin-bottom:4px"><span>${totalLabel}:</span> <strong>${total}</strong></div>` +
              `<div><span>${amountLabel}:</span> <strong>${amountFormatted}</strong></div>` +
              `</div>`
            );
          },
        },
        legend: {
          show: false,
        },
        colors: [], // populated dynamically
      };

      this.updateColorsFromTheme();
      this.updateSeries();
    });
  }

  ngOnDestroy(): void {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
  }

  onLegendItemClick(key: "claims" | "cases"): void {
    if (key === "claims") {
      this.claimsNotFiledSelected.emit();
      return;
    }

    this.filedCasesNotReimbursedSelected.emit();
  }

  private onChartBarSelected(dataPointIndex: number): void {
    if (dataPointIndex === 0) {
      this.claimsNotFiledSelected.emit();
      return;
    }

    if (dataPointIndex === 1) {
      this.filedCasesNotReimbursedSelected.emit();
    }
  }

  getLegendItemTooltip(key: "claims" | "cases"): string {
    return key === "claims"
      ? this.translateSvc.instant("dashboard.view_claims")
      : this.translateSvc.instant("dashboard.view_cases");
  }

  private updateSeries(): void {
    if (this.chartOptions) {
      this.chartOptions.series = [
        {
          name: "",
          data: [this.unFiledClaimTotal, this.filedNoReimbursementTotal],
        },
      ];

      if (this.chart?.updateOptions) {
        this.chart.updateOptions(
          {
            series: this.chartOptions.series,
            dataLabels: this.chartOptions.dataLabels,
            tooltip: this.chartOptions.tooltip,
          },
          false,
          false,
        );
      }
    }
  }

  private updateColorsFromTheme(): void {
    const root = getComputedStyle(document.documentElement);
    const primary = root.getPropertyValue("--mat-charts-color-one").trim();
    const secondary = root.getPropertyValue("--mat-charts-color-two").trim();
    this.chartOptions.colors = [primary, secondary];
    this.legendItems = [
      { key: "claims", labelKey: "dashboard.claims_not_filed", color: primary },
      {
        key: "cases",
        labelKey: "dashboard.filed_cases_not_reimbursed",
        color: secondary,
      },
    ];

    if (this.chart?.updateOptions) {
      this.chart.updateOptions(
        {
          colors: this.chartOptions.colors,
        },
        false,
        false,
      );
    }
  }
}
